<script setup lang="ts">
import { debounce } from "lodash";
import { computed } from "vue";
import { FacilityFirestore } from "~/types/facility";
import { getFontClass, getFontStyle } from "~/util/fontUtils";
import { defaultTheme, FullColorsCodes, getThemeAllColorsCodes } from "~/util/themesColors";

const emit = defineEmits(["close", "submit"]);

const props = defineProps({
  title: {
    type: String,
    required: false,
    default: () => ""
  },
  buttonText: {
    type: String,
    required: false,
    default: () => {
      const { t } = useI18n();
      return t("button.save");
    }
  },
  submitDisabled: {
    type: Boolean,
    required: false,
    default: false
  },
  whiteCloseBtn: {
    type: Boolean,
    required: false,
    default: false
  },
  uploadInProgress: {
    type: Boolean,
    required: false,
    default: false
  },
  disableSubmitOnEnter: {
    type: Boolean,
    required: false,
    default: false
  },
  showPlusIcon: {
    type: Boolean,
    required: false,
    default: false
  },
  showSubmit: {
    type: Boolean,
    required: false,
    default: true
  },
  isKioskPage: {
    type: Boolean,
    required: false,
    default: false
  }
});

// Function to disable scrolling on the body element
const disableScroll = () => {
  document.body.style.overflow = "hidden";
};

// Function to enable scrolling on the body element
const enableScroll = () => {
  document.body.style.overflow = ""; // Re-enable scrolling
};

// Call disableScroll when modal is opened
onMounted(() => {
  disableScroll();
});

// Call enableScroll when modal is closed
onUnmounted(() => {
  enableScroll();
});

const facilityStore = useFacilityStore();
const facilityStoreRefs = storeToRefs(facilityStore);
const facility: Ref<FacilityFirestore | null> = facilityStoreRefs.facility;
const font = computed(() => facility?.value?.designThemeConfig?.font || "");
const fontStyle = computed(() => getFontStyle(font.value));
const fontClass = computed(() => getFontClass(font.value));

const buttonText = computed(() => {
  const { t } = useI18n();
  if (props.uploadInProgress) {
    return t("button.please_wait");
  }

  return props.buttonText;
});

const closeModal = (event) => {
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }
  emit("close");
  enableScroll();
};

const submit = () => {
  emit("submit");
};
const submitDebounced = debounce(submit, 500);

const pressEnter = () => {
  if (!props.disableSubmitOnEnter) {
    submit();
  }
};

const themeColors = ref<FullColorsCodes>(defaultTheme);

watchEffect(() => {
  if (props.isKioskPage) {
    const colors =
      getThemeAllColorsCodes(
        facility?.value?.designThemeConfig?.colorTheme || "theme-light-green"
      ) || defaultTheme;
    themeColors.value = colors;
  }
});
</script>

<template>
  <div
    id="modal"
    class="modal fade show modal-overlay"
    tabindex="-1"
    role="dialog"
    aria-modal="true"
    @keydown.esc="closeModal"
    @keydown.enter="pressEnter"
  >
    <div class="modal-dialog" role="document">
      <div class="modal-content">
        <div class="header" :style="{ 'background-color': props.isKioskPage ? themeColors.primaryColor : '' }">
          <h3
            v-if="props.title"
            :class="['modal-title', 'cb_tiny_on_mobile', fontClass]"
            :style="fontStyle"
          >
            {{ props.title }}
          </h3>
          <button
            class="close"
            aria-label="Close"
            @click="closeModal"
            @touchend="closeModal"
          >
            <CareDayIcon
              :name="props.whiteCloseBtn ? 'closeWhiteKiosk' : 'close'"
              class="close-btn"
            />
          </button>
        </div>

        <div class="modal-body">
          <slot />
        </div>

        <div
          v-if="props.showSubmit"
          class="modal-footer"
        >
          <button
            type="submit"
            class="btn btn-footer cb_tiny_on_mobile d-flex flex-wrap justify-content-center align-items-center fs-4"
            :disabled="props.submitDisabled || props.uploadInProgress"
            @click="submitDebounced"
            @touchend="submitDebounced"
          >
            <font-awesome-icon
              v-if="showPlusIcon"
              icon="fa-plus"
              class="margin-icon"
            />
            <span
              v-show="props.uploadInProgress"
              class="spinner-grow spinner-grow-sm"
              role="status"
              aria-hidden="true"
            />
            {{ buttonText }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.modal-footer {
  border: none;
  background-color: var(--ct_D1) !important;
}

.btn {
  border: none;
}

.btn-footer {
  display: inline-block;
  width: 100%;
  font-family: var(--ct_font_medium);
  color: #0c3846;
  text-align: center;
  vertical-align: middle;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  padding: 0.375rem 0.75rem;
  font-size: 1.4em;
  line-height: 1.5;
  border-radius: 0.5rem;
  background-color: var(--ct_D1);
}

::placeholder {
  background-color: transparent !important;
  color: var(--ct_A) !important;
  font-weight: 400 !important;
  font-size: 1.25rem !important;
}

.close {
  background-color: transparent;
  border: none;
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  align-items: center;
  padding: 20px;
  justify-content: center;
}

.close-btn {
  width: 2rem;
  color: #0c3846;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #0c3846e6;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.3s ease-in-out, visibility 0.3s ease-in-out;
}

.modal-body,
.header {
  padding: 20px 40px;
}

.modal-body {
  overflow: auto;
}

.modal-dialog {
  background: #fff;
  max-width: 90%;
  width: 100%;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
  z-index: 3;
  border-radius: 1.25rem!important;
  overflow: hidden;
  margin-bottom: 5%;
}

.show.modal-overlay {
  visibility: visible;
  opacity: 1;
}

.modal-content {
  border: none;
}

.modal-title {
  color: #0c3846;
  font-weight: bold;
}

.margin-icon {
  margin: 0px 12px 8px;
}

@media screen and (min-width: 1366px) {
  .modal-dialog {
    max-width: 50%;
  }
}

@media screen and (min-width: 2600px) {
  .modal-dialog {
    max-width: 33%;
  }
}
</style>
